<template>
  <div class="project-canvas-container" v-loading="loading" element-loading-spinner="atom-audit-loader">
    <div class="canvas-header">
      <div style="width: 100%;">
        <h4 class="headerWithBorder">Project Canvases</h4>
      </div>
      <div class="canvas-actions">
        <el-button plain round @click="showAddCanvasDialog"> <i class="mdi mdi-plus" style="margin-right: 4px;"></i>Add Existing Canvas </el-button>
      </div>
    </div>

    <div class="canvas-content">
      <v-client-table v-if="projectCanvasList.length > 0" style="width: 100%; margin-bottom: 0px;" :data="projectCanvasList" class="hiaTable" :columns="['name', 'description', 'edit']" :options="canvasTableOptions" @row-click="viewCanvas">
        <template slot="edit" slot-scope="props">
          <div style="text-align: right; margin-right: 15px;">
            <el-button type="text" size="mini" style="padding: 0px;" @click="removeCanvas(props.row)"><i class="mdi mdi-delete" style="margin-right: 4px;"></i>Remove</el-button>
            <el-button type="text" size="mini" style="padding: 0px;" @click="editCanvas(props.row)"><i class="mdi mdi-pencil" style="margin-right: 4px;"></i>Edit Canvas</el-button>
          </div>
        </template>
      </v-client-table>

      <el-card v-if="!loading && projectCanvasList.length === 0" style="width: 50%">
        <div style="font-size: 13px;">No canvases added to this project. Add an existing canvas or go to <router-link to="/reports/pro-risk-analysis">Pro Risk Analysis</router-link> to create a new canvas.</div>
      </el-card>

      <div class="canvas-navigation" v-if="projectCanvasList.length > 0">
        <h4 class="canvas-navigation-text">
          <el-button v-if="canNavigateLeft()" type="text" class="navigation-button" size="mini" @click="navigateToPreviousCanvas">&laquo;</el-button>
          Showing Canvas {{ getCurrentCanvasIndex() + 1 }} of {{ projectCanvasList.length }}
          <el-button v-if="canNavigateRight()" type="text" class="navigation-button" size="mini" @click="navigateToNextCanvas">&raquo;</el-button>
        </h4>
      </div>
    </div>

    <div v-if="selectedCanvas">
      <el-card>
        <proRiskCanvas :isDashboard="true" :propCanvas="selectedCanvas" :options="[]" />
      </el-card>
    </div>

    <el-dialog title="Select Canvas" :visible.sync="loadCanvasVisible" width="80%">
      <ProRiskCanvasList v-if="loadCanvasVisible" @select="selectCanvas" />
    </el-dialog>
  </div>
</template>

<script>
  import { mapActions, mapState, mapMutations } from 'vuex'
  import ProRiskCanvasList from '@/views/riskAnalysis/ProRiskCanvasList.vue'
  import proRiskCanvas from '@/views/riskAnalysis/ProRiskCanvas.vue'

  export default {
    name: 'ProjectCanvas',
    components: {
      ProRiskCanvasList,
      proRiskCanvas
    },
    props: {
      projectId: {
        type: Number,
        required: true
      }
    },
    data() {
      return {
        loading: false,
        availableCanvases: [],
        loadCanvasVisible: false,
        selectedCanvas: null,
        canvasTableOptions: {
          filterable: false,
          filterByColumn: false,
          headings: {
            lineSequence: '#',
            reasonForChange: 'Reason for Change',
            codeType: 'Code Type',
            edit: ''
          },
          perPage: 15,
          texts: {
            filter: '',
            filterBy: '...',
            noResults: 'No Canvases Added to Project'
          },
          highlightMatches: true,
          sortIcon: { is: 'none' },
          rowClassCallback: row => {
            if (row.canvasId === this.selectedCanvas?.canvasId) {
              return 'activeCanvas'
            } else {
              return 'inactiveCanvas'
            }
          }
        },
        formData: {
          canvasId: null
        },
        rules: {
          canvasId: [{ required: true, message: 'Please select a canvas', trigger: 'change' }]
        }
      }
    },
    methods: {
      ...mapActions('proReviews/canvas/', ['GET_CANVAS_BY_PROJECT_ID', 'DELETE_CANVAS_PROJECT', 'POST_CANVAS_PROJECT']),
      ...mapMutations('proReviews/canvas/', ['SET_PROJECT_CANVAS_LIST']),
      async initialize() {
        await this.loadProjectCanvases()
        if (this.projectCanvasList.length > 0) {
          this.selectedCanvas = this.projectCanvasList[0]
        }
      },

      getCurrentCanvasIndex() {
        return this.projectCanvasList.findIndex(canvas => canvas.canvasId === this.selectedCanvas?.canvasId)
      },

      canNavigateLeft() {
        return this.getCurrentCanvasIndex() > 0
      },

      canNavigateRight() {
        return this.getCurrentCanvasIndex() < this.projectCanvasList.length - 1
      },

      navigateToPreviousCanvas() {
        if (this.canNavigateLeft()) {
          this.selectedCanvas = this.projectCanvasList[this.getCurrentCanvasIndex() - 1]
        }
      },

      navigateToNextCanvas() {
        if (this.canNavigateRight()) {
          this.selectedCanvas = this.projectCanvasList[this.getCurrentCanvasIndex() + 1]
        }
      },

      goToProRiskAnalysis() {
        this.$router.push('/reports/pro-risk-analysis')
      },

      async loadProjectCanvases() {
        this.loading = true
        try {
          await this.GET_CANVAS_BY_PROJECT_ID(this.projectId)
        } catch (error) {
          this.$message.error('Failed to load project canvases: ' + error.message)
        } finally {
          this.loading = false
        }
      },

      isSelectedCanvas(canvas) {
        return this.selectedCanvas && this.selectedCanvas.canvasId === canvas.canvasId
      },

      showAddCanvasDialog() {
        this.loadCanvasVisible = true
      },

      editCanvas(canvas) {
        this.$router.push(`/reports/pro-risk-analysis?canvasId=${canvas.canvasId}`)
      },

      async selectCanvas(canvas) {
        if (this.projectCanvasList.some(c => c.canvasId === canvas.canvasId)) {
          this.$message.error('Canvas already added to project')
          return
        }

        this.loadCanvasVisible = false
        await this.$nextTick()
        try {
          this.loading = true
          await this.POST_CANVAS_PROJECT({ canvasId: canvas.canvasId, projectId: this.projectId })
          this.SET_PROJECT_CANVAS_LIST(this.projectCanvasList.concat(canvas))
          this.$message.success('Canvas added successfully')
          this.selectedCanvas = canvas
        } catch (error) {
          if (error.message) {
            this.$message.error('Failed to add canvas: ' + error.message)
          }
        } finally {
          this.loading = false
        }
      },

      async removeCanvas(canvas) {
        try {
          await this.$confirm('Are you sure you want to remove this canvas from the project?', 'Warning', {
            confirmButtonText: 'OK',
            cancelButtonText: 'Cancel',
            type: 'warning'
          })

          await this.DELETE_CANVAS_PROJECT({ canvasId: canvas.canvasId, projectId: this.projectId })
          this.SET_PROJECT_CANVAS_LIST(this.projectCanvasList.filter(c => c.canvasId !== canvas.canvasId))
          this.selectedCanvas = this.projectCanvasList.length > 0 ? this.projectCanvasList[0] : null
          this.$message.success('Canvas removed successfully')
        } catch (error) {
          if (error.message) {
            this.$message.error('Failed to remove canvas: ' + error.message)
          }
        } finally {
          this.loading = false
        }
      },

      viewCanvas(tableRow) {
        this.selectedCanvas = tableRow.row
      }
    },
    activated() {
      this.initialize()
    },
    created() {
      this.initialize()
    },
    computed: {
      ...mapState('proReviews/canvas/', ['projectCanvasList'])
    }
  }
</script>

<style scoped>
  >>> .activeCanvas {
    background: rgba(255, 196, 12, 0.2) !important;
    /* border-top: 1px solid blue;
    border-bottom: 1px solid blue;
    border-left: 1px solid blue; */
  }

  >>> .inactiveCanvas {
    cursor: pointer;
    /* font-weight: 300; */
    /* border-right: 1px solid blue; */
  }

  >>> .VuePagination__count {
    display: none;
  }

  .project-canvas-container {
    min-height: 500px;
  }

  .canvas-header {
    margin-bottom: 20px;
  }

  .selected-canvas {
    border: 2px solid #409eff;
  }

  .canvas-content {
    padding: 0px 0px 0px 0px;
    /* border-bottom: 1px solid #e0e0e0; */
  }

  .canvas-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
  }

  .canvas-card {
    transition: all 0.3s ease;
  }

  .canvas-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .canvas-card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  .canvas-info h3 {
    margin: 0 0 8px 0;
    font-size: 1.1em;
    color: #303133;
  }

  .canvas-description {
    margin: 0;
    font-size: 0.9em;
    color: #606266;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .canvas-actions {
    display: flex;
    gap: 8px;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px;
    text-align: center;
    color: #909399;
  }

  .empty-state p {
    margin: 16px 0;
    font-size: 1.1em;
  }

  .canvas-selection {
    padding: 20px 0;
  }

  .canvas-navigation {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
  }

  .canvas-navigation-text {
    font-weight: 500;
    margin: 15px 0 0 0;
    font-size: 12px;
  }

  .navigation-button {
    font-weight: 500;
    font-size: 18px;
    margin-left: 0px;
  }
</style>
